/**
 * Script Loader Utilities
 * Conditionally load analytics and marketing scripts based on cookie consent
 */

import { hasConsent, CookiePreferences } from './cookies';

declare global {
  interface Window {
    dataLayer: unknown[];
    gtag: (...args: unknown[]) => void;
    fbq: { (...args: unknown[]): void; callMethod?: (...args: unknown[]) => void; queue?: unknown[]; push?: unknown; loaded?: boolean; version?: string };
    _fbq: (...args: unknown[]) => void;
    hj: { (...args: unknown[]): void; q?: unknown[] };
    _hjSettings: Record<string, unknown>;
    clarity: { (...args: unknown[]): void; q?: unknown[] };
    requestIdleCallback: (callback: IdleRequestCallback, options?: IdleRequestOptions) => number;
  }
}

// Track loaded scripts to prevent duplicates
const loadedScripts = new Set<string>();

/**
 * Dynamically load a script if consent is given
 */
export function loadScript(
  src: string,
  id: string,
  consentType: keyof CookiePreferences,
  onLoad?: () => void,
  onError?: () => void
): void {
  // Don't load if no consent or already loaded
  if (!hasConsent(consentType) || loadedScripts.has(id)) {
    return;
  }

  // Don't run on server-side
  if (typeof window === 'undefined') return;

  const initiateLoad = () => {
    const script = document.createElement('script');
    script.src = src;
    script.id = id;
    script.async = true;

    script.onload = () => {
      loadedScripts.add(id);
      onLoad?.();
    };

    script.onerror = () => {
      console.error(`Failed to load script: ${src}`);
      onError?.();
    };

    document.head.appendChild(script);
  };

  // Use requestIdleCallback if available, otherwise fallback to setTimeout
  if ('requestIdleCallback' in window) {
    window.requestIdleCallback(() => initiateLoad());
  } else {
    setTimeout(initiateLoad, 2000); // Wait 2 seconds
  }
}

/**
 * Load Google Analytics 4 if analytics consent is given
 */
export function loadGoogleAnalytics(measurementId: string): void {
  if (!hasConsent('analytics')) return;

  // Skip if measurementId is placeholder, invalid, or empty
  if (!measurementId || measurementId === 'GA_MEASUREMENT_ID' || measurementId.startsWith('GA_') || measurementId.trim() === '') {
    console.warn('Google Analytics: Invalid, placeholder, or empty measurement ID provided');
    return;
  }

  // Load gtag script
  loadScript(
    `https://www.googletagmanager.com/gtag/js?id=${measurementId}`,
    'google-analytics-script',
    'analytics',
    () => {
      // Initialize gtag after script loads
      if (typeof window !== 'undefined') {
        window.dataLayer = window.dataLayer || [];
        function gtag(...args: unknown[]) {
          window.dataLayer.push(args);
        }
        window.gtag = gtag as (...args: unknown[]) => void;

        gtag('js', new Date());
        gtag('config', measurementId, {
          anonymize_ip: true, // GDPR compliance
          allow_google_signals: false, // Disable Google Signals for privacy
        });
      }
    }
  );
}

/**
 * Load Facebook Pixel if marketing consent is given
 */
export function loadFacebookPixel(pixelId: string): void {
  if (!hasConsent('marketing')) return;

  if (typeof window === 'undefined') return;

  // Skip if pixelId is placeholder, invalid, or empty
  if (!pixelId || pixelId === 'FB_PIXEL_ID' || pixelId.startsWith('FB_') || pixelId.trim() === '') {
    console.warn('Facebook Pixel: Invalid, placeholder, or empty pixel ID provided');
    return;
  }

  // Initialize fbq
  if (!window.fbq) {
    const fbq = function (...args: unknown[]) {
      if (fbq.callMethod) {
        fbq.callMethod(...args);
      } else {
        fbq.queue?.push(args);
      }
    } as Window['fbq'];

    if (!window._fbq) window._fbq = fbq;
    fbq.push = fbq;
    fbq.loaded = true;
    fbq.version = '2.0';
    fbq.queue = [];
    window.fbq = fbq;

    // Load Facebook Pixel script
    loadScript(
      'https://connect.facebook.net/en_US/fbevents.js',
      'facebook-pixel-script',
      'marketing',
      () => {
        // Initialize pixel after script loads
        window.fbq('init', pixelId);
        window.fbq('track', 'PageView');
      }
    );
  }
}

/**
 * Load Google Tag Manager if marketing consent is given
 */
export function loadGoogleTagManager(containerId: string): void {
  if (!hasConsent('marketing')) return;

  if (typeof window === 'undefined') return;

  // Skip if containerId is placeholder, invalid, or empty
  if (!containerId || containerId === 'GTM_CONTAINER_ID' || containerId.startsWith('GTM_') || containerId.trim() === '') {
    console.warn('Google Tag Manager: Invalid, placeholder, or empty container ID provided');
    return;
  }

  // Initialize dataLayer
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({
    'gtm.start': new Date().getTime(),
    event: 'gtm.js'
  });

  // Load GTM script
  loadScript(
    `https://www.googletagmanager.com/gtm.js?id=${containerId}`,
    'google-tag-manager-script',
    'marketing'
  );
}

/**
 * Load Hotjar if analytics consent is given
 */
export function loadHotjar(hjid: string, hjsv: string = '6'): void {
  if (!hasConsent('analytics')) return;

  if (typeof window === 'undefined') return;

  // Skip if hjid is invalid
  if (!hjid || hjid.trim() === '') return;

  window.hj = window.hj || (function (...args: unknown[]) {
    (window.hj.q = window.hj.q || []).push(args);
  } as Window['hj']);

  window._hjSettings = { hjid: parseInt(hjid), hjsv: parseInt(hjsv) };

  loadScript(
    `https://static.hotjar.com/c/hotjar-${hjid}.js?sv=${hjsv}`,
    'hotjar-script',
    'analytics'
  );
}

/**
 * Load Microsoft Clarity if analytics consent is given
 */
export function loadClarity(projectId: string): void {
  if (!hasConsent('analytics')) return;

  if (typeof window === 'undefined') return;

  // Skip if projectId is invalid
  if (!projectId || projectId.trim() === '') return;

  window.clarity = window.clarity || (function (...args: unknown[]) {
    (window.clarity.q = window.clarity.q || []).push(args);
  } as Window['clarity']);

  loadScript(
    `https://www.clarity.ms/tag/${projectId}`,
    'clarity-script',
    'analytics'
  );
}

/**
 * Load custom analytics script
 */
export function loadCustomAnalytics(scriptUrl: string, initFunction?: () => void): void {
  loadScript(
    scriptUrl,
    'custom-analytics-script',
    'analytics',
    initFunction
  );
}

/**
 * Remove all tracking scripts and clear data
 */
export function clearTrackingScripts(): void {
  if (typeof window === 'undefined') return;

  // Remove Google Analytics
  const gaScript = document.getElementById('google-analytics-script');
  if (gaScript) {
    gaScript.remove();
    loadedScripts.delete('google-analytics-script');
  }

  // Remove Facebook Pixel
  const fbScript = document.getElementById('facebook-pixel-script');
  if (fbScript) {
    fbScript.remove();
    loadedScripts.delete('facebook-pixel-script');
  }

  // Remove Google Tag Manager
  const gtmScript = document.getElementById('google-tag-manager-script');
  if (gtmScript) {
    gtmScript.remove();
    loadedScripts.delete('google-tag-manager-script');
  }

  // Remove Hotjar
  const hjScript = document.getElementById('hotjar-script');
  if (hjScript) {
    hjScript.remove();
    loadedScripts.delete('hotjar-script');
  }

  // Remove Clarity
  const clarityScript = document.getElementById('clarity-script');
  if (clarityScript) {
    clarityScript.remove();
    loadedScripts.delete('clarity-script');
  }

  // Clear global variables
  const w = window as unknown as Record<string, unknown>;
  delete w.gtag;
  delete w.fbq;
  delete w.dataLayer;
}

export interface TrackingConfig {
  googleAnalytics?: string;
  facebookPixel?: string;
  googleTagManager?: string;
  hotjarId?: string;
  clarityId?: string;
}

const TRACKING_DEFAULTS = {
  hjsv: '6',
};

/**
 * Initialize all tracking scripts based on current consent
 */
export function initializeTracking(config: TrackingConfig): void {
  if (config.googleAnalytics) {
    loadGoogleAnalytics(config.googleAnalytics);
  }

  if (config.facebookPixel) {
    loadFacebookPixel(config.facebookPixel);
  }

  if (config.googleTagManager) {
    loadGoogleTagManager(config.googleTagManager);
  }

  if (config.hotjarId) {
    loadHotjar(config.hotjarId, TRACKING_DEFAULTS.hjsv);
  }

  if (config.clarityId) {
    loadClarity(config.clarityId);
  }
}

/**
 * Re-initialize tracking when consent changes
 */
export function handleConsentChange(
  newPreferences: CookiePreferences,
  config: TrackingConfig
): void {
  // Clear all scripts first
  clearTrackingScripts();

  // Re-initialize based on new preferences
  setTimeout(() => {
    initializeTracking(config);
  }, 100);
}
